import SidebarFloatingAd from 'components/ad/SidebarFloatingAd'
import ReviewOwnersCarousel from 'components/owner-notes/review/ReviewOwnersCarousel'
import ReviewOwnersStrip from 'components/owner-notes/review/ReviewOwnersStrip'
import LegacyReviewHeader from 'components/reviews/LegacyReviewHeader'
import ReviewHeader, { isListeningGear } from 'components/reviews/ReviewHeader'
import NC from 'nc/NC'
import React, { useMemo } from 'react'
import { useAffilate } from 'shared-components/hooks/use-affilate'
import { useTaglessAd } from 'shared-components/hooks/use-tagless-ad'
import PostNavigation from 'shared-components/layout/PostNavigation'
import ScrollEventsListener from 'shared-components/service/ScrollEventsListener'
import styleUtils from 'shared-components/styles/util.module.css'
import AdaptiveThreeColumnLayout from 'shared-components/ui/AdaptiveThreeColumnLayout'
import BreadCrumbs from 'shared-components/ui/BreadCrumbs'
import Disclaimer from 'shared-components/ui/Disclaimer'
import GoogleButtons from 'shared-components/ui/GoogleButtons'
import PostShortLine from 'shared-components/ui/PostShortLine'
import PostTags from 'shared-components/ui/PostTags'
import PostTitle from 'shared-components/ui/PostTitle'
import SidedCategoryMenu from 'shared-components/ui/SidedCategoryMenu'
import SourcesLine from 'shared-components/ui/SourcesLine'
import { DSReviewProductPageSG } from 'types.sg.ds'
import stylesCommon from './PostTemplate.ReviewTemplate.module.css'
import styles from './ReviewProductTemplate.module.css'
import { useArticleViewed } from 'shared-components/hooks/use-article-viewed'
import UnderTitleAds from 'components/ad/UnderTitleAds'
import ArticleConversation from 'shared-components/comments/conversation/ArticleConversation'
import { conversationSlot } from 'shared-components/comments/conversation/conversationSlot'
import ConversationRailCard from 'shared-components/comments/conversation/ConversationRailCard'
import { cardOwnersFromCard } from 'lib/owner-notes/card-owners'
import { GuideOwnersProvider } from 'components/owner-notes/guide/GuideOwnersProvider'
import LabSurfaces, { useLabSurfacesDocked } from 'components/product/LabSurfaces'
import { CatalogAnalyticsProvider, type CatalogAnalyticsScope } from 'lib/analytics/catalog'
import { useDeclaredState } from 'lib/owner-notes/declared'

const OWNERS_SECTION = 'review-owners'
const LAB_CHARTS = 'lab-charts'
const CHART_RESOURCES = new Set(['nc-audio-chart', 'nc-audio-charts-next'])

interface ReviewProductTemplateProps {
  page: DSReviewProductPageSG
}

const ReviewBody: React.VFC<ReviewProductTemplateProps> = ({ page }) => {
  useAffilate(page.productBars)
  useTaglessAd()
  // The mobile conversation bar yields while the LabCompare drawer / pill is docked (a pin, a
  // focus or a declared product) — not on every page with a strip.
  const labDocked = useLabSurfacesDocked()
  const conversation =
    page.pagePath && page.meta.engagementContainerId ? (
      <ArticleConversation className={stylesCommon.comments} hideBar={labDocked} />
    ) : null
  // The owner ask shows for any DealHunt product; `owners` is null until a note publishes.
  const product = page.reviewProduct
  const owners = cardOwnersFromCard(product?.owners)
  // "What owners say" sits before the verdict ("Should you buy …"), else at the end of the body.
  const verdictAt = page.blocks.findIndex(
    b => b.resource === 'nc-section' && b.anchor.startsWith('id-verdict')
  )
  const ownersAt = verdictAt === -1 ? page.blocks.length : verdictAt
  // "See all lab data" jumps to the first lab chart; a review without charts gets no link.
  const chartsAt = page.blocks.findIndex(b => CHART_RESOURCES.has(b.resource))
  // The end-of-article conversation follows "Have your say" (nc-have-your-say) when the post
  // has it (Guillaume, 2026-10-27) — else after the tags, as before.
  const conversationAt = conversationSlot(page.blocks)
  const ownersSection = product ? (
    <section id={OWNERS_SECTION} className={styles.owners}>
      <span className={styles.ownersEyebrow}>{'Before you buy'}</span>
      <h2 className={styles.ownersTitle}>{'What owners say'}</h2>
      <p className={styles.ownersSub}>
        {'What verified owners say after living with them. Not part of the SoundGuys rating.'}
      </p>
      <ReviewOwnersCarousel
        context={product}
        owners={owners}
        aggregate={page.ownersEnrichment?.aggregate ?? null}
      />
    </section>
  ) : null

  const titleBlock = (
    <>
      <PostTitle title={page.title} subtitle={page.subtitle} />
      <PostShortLine post={page} className={styles.shortLine} />
      <UnderTitleAds ads={page.underTitleAds} />
    </>
  )

  return (
    <>
      <ScrollEventsListener />
      <Disclaimer />
      <div
        className={styleUtils.wrapper}
        ref={useArticleViewed({
          articleId: page.meta?.postId?.toString() ?? null,
          title: page.title,
          url: page.pagePath,
          categoryLevel: 1,
          categoryTitle: page.meta.categories[0]?.label ?? '',
          author: page.authors.authoredBy.map(author => author.name).join(', '),
          wordCount: parseInt(page.meta.dataLayer?.word_count?.toString() ?? '0', 10),
          date: page.meta.dataLayer?.original_pub_date?.toString() ?? '',
        })}
      >
        <BreadCrumbs list={page.breadcrumbs} className={stylesCommon.breadCrumbs} />
        {/* The new header's lab column is empty outside headphones, earbuds and headsets. */}
        {!isListeningGear(page.breadcrumbs, page.reviewProduct?.full_slug) ? (
          <LegacyReviewHeader page={page}>{titleBlock}</LegacyReviewHeader>
        ) : (
          <>
            {titleBlock}
            <ReviewHeader
              className={styles.header}
              review={page}
              labDistributions={page.labDistributions}
              price={page.price}
              labDataHref={chartsAt === -1 ? null : `#${LAB_CHARTS}`}
              self={
                product
                  ? {
                      nodeId: product.node_id,
                      slug: product.full_slug,
                      name: product.name,
                      pdpHref: product.sg_href,
                      clearbuyProductId: product.compare.clearbuyProductId ?? null,
                    }
                  : null
              }
              rivals={page.rivals}
            />
          </>
        )}
        {product ? (
          <ReviewOwnersStrip
            className={styles.strip}
            context={product}
            owners={owners}
            sectionId={OWNERS_SECTION}
          />
        ) : null}
        <AdaptiveThreeColumnLayout
          left={
            page.sideMenu.length ? (
              <SidedCategoryMenu
                className={stylesCommon.sidebarPostNav}
                title="Go deeper"
                menu={page.sideMenu}
              />
            ) : null
          }
          content={
            <>
              <GoogleButtons data={page.googleButtons} />

              {page.blocks.map((item, index) => (
                <React.Fragment key={index + 1}>
                  {index === ownersAt ? ownersSection : null}
                  {index === chartsAt ? <div id={LAB_CHARTS} className={styles.anchor} /> : null}
                  <NC data={item} tpl="text" index={index} />
                  {index === conversationAt ? conversation : null}
                </React.Fragment>
              ))}
              {ownersAt === page.blocks.length ? ownersSection : null}
              {page.sources.length ? (
                <SourcesLine className={styleUtils.nc} sources={page.sources} />
              ) : null}

              <PostTags meta={page.meta} className={stylesCommon.postTags} />

              {conversationAt === -1 ? conversation : null}
            </>
          }
          sidebar={
            <PostNavigation>
              {[...page.sidebarContent, ...page.sidebarAds].map((item, index) => (
                <NC key={index} data={item} tpl="sidebar" index={index} />
              ))}
              {/* After every WordPress sidebar widget (the trust box arrives in sidebarAds as an
                  nc-multi), before the sticky Playwire ads: the widgets scroll away above the card and
                  never pass under the stuck strip; the ads still pin under it. */}
              <ConversationRailCard />
              <SidebarFloatingAd />
            </PostNavigation>
          }
        />
        {/* <RecommendSG
          containerClassName={stylesCommon.related}
          itemClassName={styleRelatedCard.relatedCard}
          title="More from SoundGuys"
        /> */}
      </div>
    </>
  )
}

/**
 * With a DealHunt product (`page.reviewProduct`) the page mounts the lab surfaces: the header's
 * "Compare these scores with" and the review's own card (its strip, when DealHunt resolved one:
 * `page.guideOwners`, `dealhuntModifyReviewPost`, 2026-10-21) pin to the same side panel, drawn
 * as the comparison (`versus`). Also the `review_page_*` scope (the card's and the owners
 * carousel's own events keep naming it too) and the one declared product, as `PostTemplate` does.
 */
const ReviewProductTemplate: React.VFC<ReviewProductTemplateProps> = props => {
  const { page } = props
  const scope = useMemo<CatalogAnalyticsScope>(
    () => ({
      page_type: 'review_page',
      ...(page.meta.categories[0]?.label ? { category: page.meta.categories[0].label } : {}),
    }),
    [page.meta.categories]
  )
  const declared = useDeclaredState()
  if (!page.reviewProduct) {
    return <ReviewBody {...props} />
  }
  const surfaces = (
    <LabSurfaces declared={declared} mountOnPin versus>
      <ReviewBody {...props} />
    </LabSurfaces>
  )
  return (
    <CatalogAnalyticsProvider value={scope}>
      {page.guideOwners ? (
        <GuideOwnersProvider page={page.guideOwners}>{surfaces}</GuideOwnersProvider>
      ) : (
        surfaces
      )}
    </CatalogAnalyticsProvider>
  )
}

export default ReviewProductTemplate
